import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import { DistributionNotFound } from "../../components/distribution-not-found";
import GuardedRoute, { type GuardedRouteProps } from "../../components/guarded-route";
import { LimitReachedCommunicate } from "../../components/limit-reached-communicate";
import { FlowPathProvider } from "../../hooks/use-flow-path";
import { useResetDistributionState } from "../../hooks/use-reset-distribution-state";
import { getConfig } from "../../models/voucher-distribution";
import { useTermsAgreementState } from "../../store/use-terms-agreement-state";
import { useUserState } from "../../store/use-user-state";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import { childPath } from "../../utils/child-path";
import { isAuthorizationRequired } from "../../utils/is-authorization-required";
import { configName } from "../config/config-name";
import { routes } from "../config/routes";
import { Catalog } from "./catalog";
import { Checkout } from "./checkout";
import { CodeValidation } from "./code-validation";
import { TermsAgreementPage } from "./terms-agreement";
import { ThankYouPage } from "./thank-you";
import { ValidateUser } from "./validate-user";

export const FriendsAndFamilyContent = () => {
  useResetDistributionState();
  const { path } = useRouteMatch();
  const { voucherDistribution } = useVouchersDistributionState();
  const { user } = useUserState();
  const acceptedInStore = useTermsAgreementState((s) => Boolean(s.acceptedConfigs[configName]));
  const config = getConfig(voucherDistribution, configName);

  if (!config) {
    return <DistributionNotFound />;
  }

  const rulesAcceptanceRequired = voucherDistribution?.rulesAcceptanceRequired ?? false;
  const isAccepted = acceptedInStore || !rulesAcceptanceRequired;
  const authorizationRequired = isAuthorizationRequired(config);

  const defaultGuardedRouteProps: GuardedRouteProps = {
    isAuthenticated: authorizationRequired ? !!user?.isValid : true,
    isAccepted,
    authenticationPath: childPath(path, routes.validateUser.raw()),
    agreementPath: childPath(path, routes.termsAgreement.raw()),
  };

  const postAuthPath = childPath(path, rulesAcceptanceRequired ? routes.termsAgreement.raw() : routes.catalog.raw());

  return (
    <FlowPathProvider value={path}>
      <Switch>
        <Route path={childPath(path, routes.thankYou.raw())} component={ThankYouPage} exact />
        <Route>
          <LimitReachedLayout>
            <Switch>
              {authorizationRequired && (
                <Route path={childPath(path, routes.validateUser.raw())} component={ValidateUser} exact />
              )}
              {!authorizationRequired && (
                <Route
                  path={childPath(path, routes.validateUser.raw())}
                  exact
                  render={() => <Redirect to={postAuthPath} />}
                />
              )}
              <Route path={childPath(path, routes.codeValidation.raw())} component={CodeValidation} />
              <Route
                path={childPath(path, routes.termsAgreement.raw())}
                render={() => {
                  if (!rulesAcceptanceRequired) {
                    return <Redirect to={childPath(path, routes.catalog.raw())} />;
                  }
                  return defaultGuardedRouteProps.isAuthenticated ? (
                    <TermsAgreementPage />
                  ) : (
                    <Redirect to={defaultGuardedRouteProps.authenticationPath} />
                  );
                }}
              />
              <GuardedRoute
                {...defaultGuardedRouteProps}
                path={childPath(path, routes.catalog.raw())}
                component={Catalog}
              />
              <GuardedRoute
                {...defaultGuardedRouteProps}
                path={childPath(path, routes.checkout.raw())}
                component={Checkout}
              />
              <Route component={DistributionNotFound} />
            </Switch>
          </LimitReachedLayout>
        </Route>
      </Switch>
    </FlowPathProvider>
  );
};

function LimitReachedLayout({ children }: { children: React.ReactNode }) {
  const { voucherDistribution } = useVouchersDistributionState();
  const config = getConfig(voucherDistribution, configName);

  if (config?.limitReached) {
    return <LimitReachedCommunicate className="mt-20" />;
  }

  return children;
}
